import { useState, useEffect, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Avatar,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  School as SchoolIcon,
  MenuBook as CurricularIcon,
  EmojiEvents as ExtracurricularIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function CoordinatorStudents() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [classes, setClasses] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalCredits: 0,
    totalCompleted: 0,
  });

  const filteredStudents = useMemo(() => {
    let filtered = students;

    if (selectedClass !== 'all') {
      filtered = filtered.filter(student => student.clasa === selectedClass);
    }

    if (selectedSpecialization !== 'all') {
      filtered = filtered.filter(student => student.specializare === selectedSpecialization);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        student.nume.toLowerCase().includes(term) ||
        student.prenume.toLowerCase().includes(term) ||
        student.email.toLowerCase().includes(term) ||
        student.clasa?.toLowerCase().includes(term) ||
        student.specializare?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [students, selectedClass, selectedSpecialization, searchTerm]);

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadAsync = async () => {
      if (profile && profile.rol === 'coordonator' && isMounted && selectedYear) {
        await loadStudents(isMounted);
      }
    };
    
    loadAsync();
    return () => { isMounted = false; };
  }, [profile, location.pathname, selectedYear]);

  const loadStudents = async (isMounted = true) => {
    setLoading(true);
    try {
      // tipurile de credit pentru mapping categorii
      const { data: creditTypes } = await supabase
        .from(TABLES.credit_types)
        .select('id, nume_tip, categorie');

      if (!isMounted) return;

      const creditTypesMap = {};
      creditTypes?.forEach(ct => {
        creditTypesMap[ct.id] = ct.categorie || ct.nume_tip?.toLowerCase() || '';
      });

      const { data: studentsData, error: studentsError } = await supabase
        .from(TABLES.profiles)
        .select('*')
        .eq('rol', 'elev')
        .order('clasa')
        .order('nume');

      if (studentsError) throw studentsError;

      const yearFilter = selectedYear === 'all' ? yearOptions : [selectedYear || getCurrentSchoolYear()];

      const studentsWithCredits = await Promise.all(
        (studentsData || []).map(async (student) => {
          // cereri validate pentru breakdown pe categorii
          const { data: requestsData } = await supabase
            .from(TABLES.credit_requests)
            .select('status, id_tip_credit, credite_solicitate, credite_acordate')
            .eq('id_elev', student.id)
            .in('an_scolar', yearFilter)
            .eq('status', 'validat');
          
          // credite pe categorii
          let crediteCurriculare = 0;
          let crediteExtracurriculare = 0;
          let crediteTransferabile = 0;

          (requestsData || []).forEach(req => {
            const categorie = creditTypesMap[req.id_tip_credit] || '';
            const credite = req.credite_acordate || req.credite_solicitate || 0;
            if (categorie === 'curricular' || (categorie.includes && categorie.includes('curricular') && !categorie.includes('extra'))) {
              crediteCurriculare += credite;
            } else if (categorie === 'extracurricular' || (categorie.includes && categorie.includes('extracurricular'))) {
              crediteExtracurriculare += credite;
            } else if (categorie === 'transferabil' || (categorie.includes && categorie.includes('transfer'))) {
              crediteTransferabile += credite;
            }
          });

          // total credite validate
          const totalCredits = crediteCurriculare + crediteExtracurriculare + crediteTransferabile;

          return {
            ...student,
            totalCredits,
            crediteCurriculare,
            crediteExtracurriculare,
            crediteTransferabile,
            activeModules: 0,
          };
        })
      );

      setStudents(studentsWithCredits);

      const uniqueClasses = [...new Set(studentsWithCredits.map(s => s.clasa).filter(Boolean))];
      const uniqueSpecializations = [...new Set(studentsWithCredits.map(s => s.specializare).filter(Boolean))];
      
      setClasses(uniqueClasses.sort());
      setSpecializations(uniqueSpecializations.sort());

      const totalStudents = studentsWithCredits.length;
      const totalCredits = studentsWithCredits.reduce((sum, s) => sum + s.totalCredits, 0);
      const totalActive = studentsWithCredits.reduce((sum, s) => sum + s.activeModules, 0);

      setStats({
        totalStudents,
        totalCredits,
        totalActive,
      });

    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  
  
  

  const getSpecializationName = (code) => {
    const specializationsMap = {
      'MI': 'Matematică-Informatică',
      'SN': 'Științe ale Naturii',
      'SS': 'Științe Sociale',
      'FL': 'Filologie',
      'BU': 'Business',
    };
    return specializationsMap[code] || code;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Toți Elevii
          </Typography>
          {selectedYear && (
            <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} sx={{ mb: 1, minWidth: 160 }} />
          )}
        </Box>
        <Typography variant="body1" color="text.secondary">
          Gestionează și monitorizează toți elevii din sistem
        </Typography>
      </Box>

      {/* Statistici generale - mai simple */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold">
                {stats.totalStudents || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Elevi
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="primary" fontWeight="bold">
                {stats.totalCredits || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Credite Totale
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main" fontWeight="bold">
                {stats.totalActive || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Module Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold">
                {classes.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clase Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtre și Căutare
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Caută elev..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Clasă"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">Toate clasele</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Specializare"
                value={selectedSpecialization}
                onChange={(e) => setSelectedSpecialization(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">Toate specializările</option>
                {specializations.map((spec) => (
                  <option key={spec} value={spec}>
                    {getSpecializationName(spec)}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Chip
                label={`${filteredStudents.length} elevi afișați`}
                color="primary"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabel elevi simplificat */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lista Elevilor
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Elev</TableCell>
                  <TableCell>Clasă</TableCell>
                  <TableCell>Specializare</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Credite Curriculare">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <CurricularIcon fontSize="small" color="primary" />
                        <span>Curr.</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Credite Extracurriculare">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <ExtracurricularIcon fontSize="small" color="warning" />
                        <span>Extra.</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Credite Transferabile">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <TransferIcon fontSize="small" color="success" />
                        <span>Trans.</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell align="center">Module</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.map((student) => (
                  <TableRow 
                    key={student.id} 
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => navigate(`/student/${student.id}`)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {student.prenume?.[0]}{student.nume?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {student.nume} {student.prenume}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {student.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Chip label={student.clasa || 'N/A'} size="small" />
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={getSpecializationName(student.specializare) || 'N/A'} 
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Chip 
                        icon={<CurricularIcon />}
                        label={student.crediteCurriculare || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Chip 
                        icon={<ExtracurricularIcon />}
                        label={student.crediteExtracurriculare || 0}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Chip 
                        icon={<TransferIcon />}
                        label={student.crediteTransferabile || 0}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {student.totalCredits || 0}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      {student.activeModules > 0 ? (
                        <Chip 
                          label={student.activeModules} 
                          color="info" 
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          0
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredStudents.length === 0 && (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <SchoolIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Nu s-au găsit elevi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Încearcă să schimbi filtrele de căutare
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}